/**
 * Kalib AI — Sign-up (Screen 1 of the revised setup flow).
 *
 * Spec rules (kalib-signup-and-reveal-setup-flow-revised.md):
 *   - Fields: First name, Email, Password.
 *   - "No copy. No trust statements. No confidentiality sermon. The form is
 *     the design."
 *   - Buttons: Continue (single primary). Sign-in link in compact secondary
 *     row only (the form is the centrepiece).
 *
 * On submit: register → if user is verified (admin) jump straight to /intake;
 * otherwise route to /verify-email so they can exchange the 6-digit code.
 *
 * Returning visitors (Back from Screen 2) — values are preserved via
 * sessionStorage so the user doesn't lose what they typed.
 */
import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { BetaBadge } from "@/components/BetaBadge";
import { KalibLogo } from "@/components/KalibLogo";

const DRAFT_KEY = "kalib_signup_draft_v1";

export default function SignUpPage() {
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null);  // { message, kind, cta } | string | null
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const next = searchParams.get("next") || "/intake";
  const signInHref = next && next !== "/intake" ? `/signin?next=${encodeURIComponent(next)}` : "/signin";

  // Restore values when the user comes Back from /verify-email.
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(DRAFT_KEY);
      if (!raw) return;
      const draft = JSON.parse(raw);
      if (draft?.firstName) setFirstName(draft.firstName);
      if (draft?.email) setEmail(draft.email);
    } catch {
      /* ignore */
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      // Persist draft first so Back from /verify-email restores values.
      sessionStorage.setItem(DRAFT_KEY, JSON.stringify({ firstName, email }));
      const data = await register(email, password, firstName);
      if (data?.email_verified) {
        // Admin / pre-verified path — skip Screen 2 entirely.
        sessionStorage.removeItem(DRAFT_KEY);
        navigate(next);
      } else {
        // Standard new-user path → 6-digit code screen.
        navigate(`/verify-email?next=${encodeURIComponent(next)}`);
      }
    } catch (err) {
      setError(formatApiError(err, { structured: true }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-100 flex flex-col" data-testid="signup-page">
      <header className="border-b border-zinc-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center gap-2">
            <Link
              to="/"
              className="block"
              data-testid="signup-logo-link"
              aria-label="Kalib AI home"
            >
              <KalibLogo variant="light" height={28} testId="signup-header-logo" />
            </Link>
            <BetaBadge />
          </div>
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-sm">
          {/* No marketing copy. No trust statements. The form is the design. */}
          <h1
            className="text-2xl sm:text-3xl font-medium tracking-tight text-zinc-950 font-heading mb-6"
            data-testid="signup-headline"
          >
            Create your account
          </h1>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="signup-form">
            {error && (
              <div
                className="text-sm text-red-600 bg-red-50 border border-red-200 px-4 py-3"
                data-testid="signup-error"
                role="alert"
              >
                <p>{typeof error === "string" ? error : error.message}</p>
                {typeof error === "object" && error?.cta?.to && (
                  <Link
                    to={error.cta.to + (next && next !== "/intake" ? `?next=${encodeURIComponent(next)}` : "")}
                    className="inline-block mt-2 text-sm font-medium underline text-red-700 hover:text-red-800"
                    data-testid="signup-error-cta"
                  >
                    {error.cta.label} →
                  </Link>
                )}
              </div>
            )}

            <div>
              <label className="text-xs font-medium uppercase tracking-widest text-zinc-900 mb-1.5 block">
                First name
              </label>
              <Input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
                autoComplete="given-name"
                className="rounded-none border-zinc-200 focus:ring-amber-700 focus:border-amber-700"
                data-testid="signup-first-name-input"
              />
            </div>

            <div>
              <label className="text-xs font-medium uppercase tracking-widest text-zinc-900 mb-1.5 block">
                Email
              </label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                className="rounded-none border-zinc-200 focus:ring-amber-700 focus:border-amber-700"
                data-testid="signup-email-input"
              />
            </div>

            <div>
              <label className="text-xs font-medium uppercase tracking-widest text-zinc-900 mb-1.5 block">
                Password
              </label>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  autoComplete="new-password"
                  className="rounded-none border-zinc-200 focus:ring-amber-700 focus:border-amber-700 pr-10"
                  data-testid="signup-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center justify-center w-7 h-7 text-zinc-500 hover:text-amber-700 transition-colors"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  aria-pressed={showPassword}
                  tabIndex={-1}
                  data-testid="signup-password-toggle"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" strokeWidth={1.75} /> : <Eye className="w-4 h-4" strokeWidth={1.75} />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-amber-700 text-white hover:bg-amber-800 rounded-none text-sm font-medium h-10"
              data-testid="signup-submit-btn"
            >
              {loading ? "Continuing..." : "Continue"}
            </Button>
          </form>

          <p className="mt-6 text-sm text-zinc-700 text-center" data-testid="signup-signin-row">
            Already have an account?{" "}
            <Link
              to={signInHref}
              className="text-amber-700 hover:text-amber-800 font-medium"
              data-testid="signup-signin-link"
            >
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
